import React, { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress,
  Chip,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from '@mui/material';
import { Group as GroupIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useSearchOrganizations } from '../../../api/hooks';
import { useGroups } from '../../../api/hooks/useGroups';
import { useBatchEnrollTraining, useBatchEnrollTrainingByGroup } from '../../../api/hooks/useTrainingEnrollments';
import { GroupDto, OrganizationSearchParams } from '../../../api/types/dtos';

interface BatchEnrollmentModalProps {
  open: boolean;
  onClose: () => void;
  trainingId: string;
}

const BatchEnrollmentModal: React.FC<BatchEnrollmentModalProps> = ({
  open,
  onClose,
  trainingId,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(new Set());
  const [selectedGroup, setSelectedGroup] = useState<GroupDto | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();

  // Build search parameters with required parentOrganizationId
  const searchParams: OrganizationSearchParams = useMemo(
    () => ({
      parentOrganizationId: selectedOrganization?.id || '',
      searchTerm: searchTerm.trim() || undefined,
      page,
      size: rowsPerPage,
      sortBy: 'name',
      sortDirection: 'ASC',
    }),
    [selectedOrganization?.id, searchTerm, page, rowsPerPage]
  );

  // Fetch organizations with proper error handling
  // Only fetch when no group is selected
  const {
    data: organizationsResponse,
    isLoading: orgsLoading,
    error: orgsError,
  } = useSearchOrganizations(searchParams, {
    enabled: open && !!selectedOrganization?.id && !selectedGroup,
  } as any);

  const { data: groupsData, isLoading: groupsLoading } = useGroups(
    selectedOrganization?.id || '',
    { isActive: true }
  );
  const groupsResponse: GroupDto[] = (groupsData as any) || [];

  const batchEnrollMutation = useBatchEnrollTraining(
    selectedOrganization?.id || '',
    trainingId
  );

  const batchEnrollByGroupMutation = useBatchEnrollTrainingByGroup(
    selectedOrganization?.id || '',
    trainingId
  );

  // Extract organizations from response
  const organizations = organizationsResponse?.data?.content || [];
  const totalOrganizations = organizationsResponse?.data?.totalElements || 0;

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const handleToggleOrg = (orgId: string) => {
    const newSelection = new Set(selectedOrgIds);
    if (newSelection.has(orgId)) {
      newSelection.delete(orgId);
    } else {
      newSelection.add(orgId);
    }
    setSelectedOrgIds(newSelection);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = organizations.map((org) => org.id).filter((id): id is string => !!id);
      setSelectedOrgIds(new Set(allIds));
    } else {
      setSelectedOrgIds(new Set());
    }
  };

  const handleSelectFromGroup = (group: GroupDto | null) => {
    setSelectedGroup(group);
    // Clear selected organizations when a group is selected
    setSelectedOrgIds(new Set());
    // Clear search term when selecting a group
    setSearchTerm('');
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSubmit = async () => {
    // If a group is selected, use the group enrollment endpoint
    if (selectedGroup?.id) {
      try {
        const result = await batchEnrollByGroupMutation.mutateAsync(selectedGroup.id);
        // result is already the array from the hook (.then(res => res.data))
        const enrolledCount = Array.isArray(result) ? result.length : 0;
        
        enqueueSnackbar(
          `Grupo "${selectedGroup.name}" inscrito exitosamente. ${enrolledCount} organizaciones inscritas`,
          { variant: 'success' }
        );
        handleClose();
      } catch (error) {
        enqueueSnackbar('Error al inscribir el grupo', { variant: 'error' });
        console.error('Error enrolling group:', error);
      }
      return;
    }

    // Otherwise, use the regular batch enrollment with selected organizations
    if (selectedOrgIds.size === 0) {
      enqueueSnackbar('Debe seleccionar al menos una organización o un grupo', {
        variant: 'warning',
      });
      return;
    }

    try {
      await batchEnrollMutation.mutateAsync({
        organizationIds: Array.from(selectedOrgIds),
        groupId: undefined,
      });

      enqueueSnackbar(`${selectedOrgIds.size} organizaciones inscritas exitosamente`, {
        variant: 'success',
      });
      handleClose();
    } catch (error) {
      enqueueSnackbar('Error al inscribir organizaciones', { variant: 'error' });
      console.error('Error enrolling organizations:', error);
    }
  };

  const handleClose = () => {
    setSelectedOrgIds(new Set());
    setSelectedGroup(null);
    setSearchTerm('');
    setPage(0);
    onClose();
  };

  const isSelected = (id: string | undefined) => {
    return id ? selectedOrgIds.has(id) : false;
  };

  const numSelected = selectedOrgIds.size;
  const rowCount = organizations.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Inscribir Organizaciones a Capacitación</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {/* Group Selector */}
          <Autocomplete<GroupDto>
            value={selectedGroup}
            onChange={(_, newValue) => handleSelectFromGroup(newValue)}
            options={groupsResponse}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Seleccionar Grupo"
                placeholder="Buscar grupo..."
                helperText={selectedGroup 
                  ? "Se inscribirán automáticamente todas las organizaciones activas del grupo seleccionado"
                  : "Seleccione un grupo para inscribir todas sus organizaciones automáticamente"}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <GroupIcon color="action" sx={{ mr: 1 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            loading={groupsLoading}
          />

          {selectedGroup && (
            <Chip
              label={`Grupo seleccionado: ${selectedGroup.name}`}
              onDelete={() => {
                handleSelectFromGroup(null);
              }}
              color="primary"
              variant="outlined"
            />
          )}

          {/* Organization Search and List - Hidden when group is selected */}
          {!selectedGroup && (
            <>
              {/* Search Bar */}
              <TextField
                label="Buscar Organizaciones"
                placeholder="Buscar por nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                fullWidth
                size="small"
              />
            </>
          )}

          {/* Error handling */}
          {!selectedGroup && orgsError && (
            <Typography color="error" variant="body2">
              Error al cargar organizaciones. Por favor, intente nuevamente.
            </Typography>
          )}

          {/* Organizations Table - Hidden when group is selected */}
          {!selectedGroup && (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 440 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={handleSelectAll}
                        disabled={orgsLoading || rowCount === 0}
                      />
                    </TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Ciudad</TableCell>
                    <TableCell>Departamento</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgsLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : organizations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No se encontraron organizaciones
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    organizations.map((org) => {
                      const isItemSelected = isSelected(org.id);
                      return (
                        <TableRow
                          hover
                          onClick={() => org.id && handleToggleOrg(org.id)}
                          role="checkbox"
                          aria-checked={isItemSelected}
                          tabIndex={-1}
                          key={org.id}
                          selected={isItemSelected}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox checked={isItemSelected} />
                          </TableCell>
                          <TableCell>{org.name}</TableCell>
                          <TableCell>N/A</TableCell>
                          <TableCell>N/A</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[25, 50, 100]}
              component="div"
              count={totalOrganizations}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          )}

          {/* Selection Summary */}
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {selectedGroup
              ? `Se inscribirán todas las organizaciones del grupo "${selectedGroup.name}"`
              : numSelected > 0
              ? `${numSelected} organizaciones seleccionadas`
              : 'Seleccione un grupo o las organizaciones para inscribir'}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleClose} 
          disabled={batchEnrollMutation.isPending || batchEnrollByGroupMutation.isPending}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            (batchEnrollMutation.isPending || batchEnrollByGroupMutation.isPending) ||
            (!selectedGroup && numSelected === 0)
          }
          startIcon={(batchEnrollMutation.isPending || batchEnrollByGroupMutation.isPending) && (
            <CircularProgress size={20} />
          )}
        >
          {(batchEnrollMutation.isPending || batchEnrollByGroupMutation.isPending)
            ? 'Inscribiendo...'
            : selectedGroup
            ? `Inscribir Grupo "${selectedGroup.name}"`
            : `Inscribir (${numSelected})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchEnrollmentModal;

